import { Bookmark, BookmarkBorder } from '@mui/icons-material';
import { Card, Checkbox, Divider, List, ListItem, Pagination, Paper, Stack, TableRow } from '@mui/material';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, { useEffect } from 'react';
import { useAsyncDebounce } from 'react-table';
import { Search } from './search/search';

export const BasicList = ({
  columns,
  data,
  enableGlobalFilter,
  enableColumnFilter,
  multiSelect = false,
  singleSelect = false,
  onRowSelected = () => {},
  onRowsSelected = () => {},
}) => {
  const [rowSelection, setRowSelection] = React.useState({});
  const [_columns, setColumns] = React.useState([]);

  if (!columns) throw new Error('columns not defined');

  useEffect(() => {
    singleSelect && onRowSelected(table.getSelectedRowModel().flatRows[0]?.original);
    multiSelect && onRowsSelected(table.getSelectedRowModel().flatRows.map((x) => x.original));
  }, [rowSelection]);

  const initColumns = () => {
    if (multiSelect || singleSelect) {
      setColumns([
        {
          id: 'select',
          header: ({ table }) =>
            multiSelect && (
              <IndeterminateCheckbox
                {...{
                  checked: table.getIsAllPageRowsSelected(),
                  indeterminate: table.getIsSomePageRowsSelected(),
                  onChange: table.getToggleAllPageRowsSelectedHandler(),
                }}
              />
            ),
          cell: ({ row }) =>
            (singleSelect || multiSelect) && (
              <IndeterminateCheckbox
                {...{
                  checked: row.getIsSelected(),
                  disabled: !row.getCanSelect(),
                  indeterminate: row.getIsSomeSelected(),
                  onChange: row.getToggleSelectedHandler(),
                }}
              />
            ),
        },
        ...columns,
      ]);
    } else {
      setColumns([...columns]);
    }
  };

  useEffect(() => {
    if (_columns.length > 0) return;
    initColumns();
  }, [columns]);

  const table = useReactTable({
    data: data,
    columns: _columns,
    state: {
      rowSelection,
    },
    //enableRowSelection: singleSelect || multiSelect, //enable row selection for all rows
    enableMultiRowSelection: multiSelect,
    enableRowSelection: (row) => {
      if (singleSelect || multiSelect) {
        return row.original.selectable;
      }
      return false;
    }, // or enable row selection conditionally per row
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    //debugTable: true,
    enableGlobalFilter: true,
  });

  const onChange = useAsyncDebounce((value) => table.setGlobalFilter(value || undefined), 200);

  return (
    <React.Fragment>
      <Paper>
        {enableGlobalFilter && (
          <div key="globalsearch" sx={{ my: 3 }}>
            <Search onChange={onChange} />
          </div>
        )}
        {table.getHeaderGroups().map((headerGroup) =>
          table.getRowModel().rows.map((row) => {
            return (
              <Card sx={{ p: 0, m: 1 }} variant="outlined">
                <List sx={{ m: 0, p: 0 }}>
                  {headerGroup.headers.map((header, index) => {
                    var cell = row.getVisibleCells()[header.index];

                    return (
                      <>
                        <ListItem>
                          <Stack
                            flexWrap="wrap"
                            sx={{ width: '100%' }}
                            flex
                            flexDirection="row"
                            alignItems="center"
                            gap={2}
                            justifyContent="space-between"
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </Stack>
                        </ListItem>
                        {headerGroup.headers.length > index + 1 && <Divider />}
                      </>
                    );
                  })}
                </List>
              </Card>
            );
          })
        )}
        <Stack flex flexDirection="row" justifyContent="center" sx={{ width: '100%', mt: 2 }}>
          {table.getPageCount() > 1 && (
            <Pagination
              page={table.getState().pagination.pageIndex + 1}
              count={table.getPageCount()}
              disabled={table.getState().pagination.pageSize === 0}
              onChange={(_, page) => table.setPageIndex(page - 1)}
            />
          )}
        </Stack>
      </Paper>
    </React.Fragment>
  );
};

function IndeterminateCheckbox({ indeterminate, ...rest }) {
  return (
    <Checkbox
      icon={<BookmarkBorder />}
      checkedIcon={<Bookmark />}
      checked={rest.checked}
      onChange={rest.onChange}
      disabled={rest.disabled}
      indeterminate={!rest.checked && indeterminate}
      color="secondary"
    />
  );
}
